import type { PluginListingEntry } from '@crowdex/agent-contracts/api';
import {
  contributionActive,
  isListedInAllApps,
  pluginSkillResourceUri,
  type WorkbenchPanelContribution,
} from '@crowdex/plugin-contracts';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import { CORE_PANEL_OWNER, CORE_PANELS } from './core-panels';

export const contributionId = (plugin: string, id: string): `plugin:${string}` =>
  `plugin:${plugin}/${id}`;
export type PluginPanel = WorkbenchPanelContribution & {
  plugin: string;
  view: SidePanelView;
  version: string;
  /** An app's own icon (`display.icon`), a template image the core paints. */
  iconSrc?: string | undefined;
  /**
   * An app's artifact types (`types` in `crowdex.json`): what its Library tile lists, and the
   * guided creation of a type (`create`) whose skill the app offers (`uri`, its `skill://` id).
   */
  artifactTypes?: readonly ArtifactTypeEntry[] | undefined;
  /** Example prompts its home offers (`page.examples`). */
  examples?: readonly { title: string; prompt: string }[] | undefined;
  /** The Crowlingo application it brings (`service` in `crowdex.json`). */
  service?: string | undefined;
  /** Not one of All apps' and the favourites' (`isListedInAllApps`), though it has a page. */
  unlisted?: true | undefined;
};
/** An artifact type of an app, as its home and the Library read it. */
export type ArtifactTypeEntry = {
  type: string;
  title: string;
  create?: { skill: string; label?: string | undefined; uri: string } | undefined;
};
/** An action of a Crowdex app (`crowdex.json` `actions`). */
export type AppAction = {
  plugin: string;
  pluginTitle: string;
  id: string;
  label: string;
  icon?: string | undefined;
  where: 'composer' | 'artifact' | 'card';
  when?: { types?: string[] | undefined } | undefined;
  state?: string | undefined;
  confirm?: string | undefined;
  run:
    | {
        open: string;
        in?: 'main' | 'side' | 'dialog' | 'window' | undefined;
        input?: unknown;
        size?: 'default' | 'wide' | undefined;
      }
    | { ask: string }
    | { call: string };
};
export type PluginWorkbench = {
  /** The enabled Crowdex apps. */
  enabledPlugins: string[];
  /** Human-readable labels keyed by stable, normalized app id. */
  pluginDisplayNames: Readonly<Record<string, string>>;
  /** The skills' resources of each app, for the context the agent gets of the app in use. */
  pluginSkills: Readonly<Record<string, readonly string[]>>;
  panels: PluginPanel[];
  /** Views the apps show during voice calls (an avatar). */
  callViews: { plugin: string; title: string; path: string }[];
  /** Actions of Crowdex apps, rendered by the core where they say. */
  actions: AppAction[];
  /** The enabled services (GitHub, Slack): no page, ⌘K opens their settings page. */
  services?: { plugin: string; title: string; iconSrc?: string | undefined }[];
  errors: string[];
  revision: number;
  loaded: boolean;
};
export const EMPTY_WORKBENCH: PluginWorkbench = {
  enabledPlugins: [],
  pluginDisplayNames: {},
  pluginSkills: {},
  panels: [],
  callViews: [],
  actions: [],
  errors: [],
  revision: 0,
  loaded: false,
};

/** The workbench of a workspace: the core's own panels, then those of its apps. */
export function buildPluginWorkbench(
  context: {
    workspaceMatches: ReadonlySet<string>;
    capabilities: ReadonlySet<string>;
  },
  revision = 0,
  apps: readonly PluginListingEntry[] = [],
): PluginWorkbench {
  const result: PluginWorkbench = {
    ...EMPTY_WORKBENCH,
    enabledPlugins: [],
    pluginDisplayNames: {},
    pluginSkills: {},
    panels: CORE_PANELS.filter((panel) => contributionActive(panel.when, context)).map((panel) => ({
      ...panel,
      plugin: CORE_PANEL_OWNER,
      view: contributionId(CORE_PANEL_OWNER, panel.id),
      version: 'core',
    })),
    callViews: [],
    actions: [],
    services: [],
    errors: [],
    revision,
    loaded: true,
  };
  for (const app of apps) if (app.enabled) addApp(result, app);
  return result;
}

/** The id of an app's own tab (`page` in `crowdex.json`): `plugin:<plugin>/page`. */
const APP_PANEL = 'page';

/** The panel of an app's own tab, and the nav rail entry that opens it. */
export function railPanels(workbench: Pick<PluginWorkbench, 'panels'>): PluginPanel[] {
  return workbench.panels.filter((panel) => panel.rail !== undefined);
}

/**
 * A type's guided creation (`create` in `crowdex.json`), when the app offers its skill (the user
 * did not turn it off): its `skill://` id, the composer reference that runs it.
 */
function creationOf(
  create: unknown,
  plugin: string,
  skills: readonly string[],
): NonNullable<ArtifactTypeEntry['create']> | null {
  if (typeof create !== 'object' || create === null) return null;
  const { skill, label } = create as { skill?: unknown; label?: unknown };
  if (typeof skill !== 'string') return null;
  const uri = pluginSkillResourceUri(plugin, skill);
  if (!skills.includes(uri)) return null;
  return { skill, uri, ...(typeof label === 'string' ? { label } : {}) };
}

/** A Crowdex app's tab and actions, as `crowdex.json` declares them. */
function addApp(result: PluginWorkbench, app: PluginListingEntry): void {
  result.enabledPlugins.push(app.name);
  (result.pluginDisplayNames as Record<string, string>)[app.name] = app.display.title;
  (result.pluginSkills as Record<string, readonly string[]>)[app.name] = app.skills;
  const page = app.page as
    | {
        title: string;
        path?: string;
        url?: unknown;
        sidebars?: { id: string; title: string; route: string }[];
        examples?: { title: string; prompt: string }[];
      }
    | undefined;
  const artifactTypes = app.types.flatMap((type): ArtifactTypeEntry[] => {
    if (typeof type['type'] !== 'string' || typeof type['title'] !== 'string') return [];
    const create = creationOf(type['create'], app.name, app.skills);
    return [{ type: type['type'], title: type['title'], ...(create === null ? {} : { create }) }];
  });
  const media = app.permissions.flatMap((permission) =>
    permission === 'media.camera'
      ? ['camera' as const]
      : permission === 'media.microphone'
        ? ['microphone' as const]
        : [],
  );
  // Served by the app, or a remote web app whose origin the server resolves.
  const view =
    page?.path !== undefined
      ? app.hasApp
        ? {
            appView: {
              plugin: app.name,
              path: page.path,
              ...(media.length > 0 ? { media } : {}),
            },
          }
        : null
      : page?.url !== undefined
        ? { remoteView: { plugin: app.name } }
        : null;
  if (page !== undefined && view !== null)
    result.panels.push({
      id: APP_PANEL,
      title: page.title,
      iconSrc: app.iconUrl,
      ...view,
      rail: page.sidebars === undefined ? {} : { sidebars: page.sidebars },
      // Its page opens from its home and ⌘K; All apps lists only apps and editors.
      ...(isListedInAllApps({ kind: app.kind, page }) ? {} : { unlisted: true }),
      plugin: app.name,
      view: contributionId(app.name, APP_PANEL),
      version: app.digest,
      description: app.display.description,
      ...(artifactTypes.length > 0 ? { artifactTypes } : {}),
      ...(page.examples === undefined ? {} : { examples: page.examples }),
      ...(app.service === undefined ? {} : { service: app.service }),
    });
  if (app.kind === 'service')
    result.services?.push({ plugin: app.name, title: app.display.title, iconSrc: app.iconUrl });
  if (app.call !== undefined && app.hasApp)
    result.callViews.push({ plugin: app.name, title: app.display.title, path: app.call.view });
  for (const action of app.actions as Omit<AppAction, 'plugin' | 'pluginTitle'>[])
    result.actions.push({ ...action, plugin: app.name, pluginTitle: app.display.title });
}
